import { useEffect, useRef, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, ShieldOff, ShieldCheck, ShieldAlert, Trash2 } from "lucide-react";
import DeleteUserModal from "@/components/DeleteUserModal";

const STATUS_COLOR = { active: "text-[#00e676]", suspended: "text-[#ff3333]", disabled: "text-[#ff3333]" };

// Only two account-level authorities exist on Project Mint. Everything
// else (Artist Owner / Team / Staff, Venue Manager / Staff, etc.) is a
// relationship stored on membership rows — not on the user doc.
const PLATFORM_ROLES = [
    { id: "user", label: "User" },
    { id: "admin", label: "Platform Admin" },
];

// Human-readable relationship labels shared with the workspace UI.
const ARTIST_ROLE_LABEL = {
    owner: "Artist Owner",
    manager: "Artist Manager",
    team: "Artist Team",
    staff: "Event Staff",
};
const VENUE_ROLE_LABEL = {
    manager: "Venue Manager",
    staff: "Venue Staff",
};

export default function AdminUsers() {
    const [q, setQ] = useState("");
    const [platformRole, setPlatformRole] = useState("");
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // Monotonically increasing request id: any response older than the
    // latest issued request is discarded, so the initial unfiltered mount
    // response can never clobber a subsequent filtered list. Combined
    // with a 250ms debounce on the search input this eliminates the
    // "stale response wins" flicker on large user tables.
    const reqSeqRef = useRef(0);
    const [debouncedQ, setDebouncedQ] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 250);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        const seq = ++reqSeqRef.current;
        setLoading(true);
        endpoints.adminUsers({
            q: debouncedQ || undefined,
            role: platformRole || undefined,
            status: status || undefined,
        })
            .then((d) => {
                if (seq !== reqSeqRef.current) return; // stale
                setRows(d.users);
            })
            .finally(() => {
                if (seq !== reqSeqRef.current) return;
                setLoading(false);
            });
    }, [debouncedQ, platformRole, status]);

    const reload = () => {
        const seq = ++reqSeqRef.current;
        setLoading(true);
        endpoints.adminUsers({
            q: debouncedQ || undefined,
            role: platformRole || undefined,
            status: status || undefined,
        })
            .then((d) => {
                if (seq !== reqSeqRef.current) return;
                setRows(d.users);
            })
            .finally(() => {
                if (seq !== reqSeqRef.current) return;
                setLoading(false);
            });
    };

    const changePlatformRole = async (u, newRole) => {
        const currentPr = u.platform_role || (u.role === "admin" ? "admin" : "user");
        if (currentPr === newRole) return;
        const msg = newRole === "admin"
            ? `Promote ${u.email} to Platform Admin? They will gain full platform authority.`
            : `Remove Platform Admin authority from ${u.email}?`;
        if (!window.confirm(msg)) return;
        try {
            await endpoints.adminUpdateUser(u.id, { platform_role: newRole });
            toast.success("Platform authority updated");
            reload();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const toggleStatus = async (u) => {
        const nextStatus = u.status === "suspended" ? "active" : "suspended";
        const msg = `${nextStatus === "suspended" ? "Suspend" : "Restore"} ${u.email}? ${nextStatus === "suspended" ? "They will not be able to log in." : ""}`;
        if (!window.confirm(msg)) return;
        try {
            await endpoints.adminUpdateUser(u.id, { status: nextStatus });
            toast.success(nextStatus === "suspended" ? "User suspended" : "User restored");
            reload();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="pm-fade" data-testid="admin-users">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Manage</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Users</h1>
                <p className="mt-2 text-xs font-mono uppercase text-[#666] max-w-2xl">
                    Platform authority is separate from workspace relationships. Artist and Venue access is
                    granted through the workspace membership screens, not from this page.
                </p>
            </header>

            <section className="px-4 md:px-8 py-4 flex flex-col md:flex-row gap-3 border-b border-[#2a2a2a] sticky top-0 bg-[#0a0a0a] z-10">
                <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 flex-1">
                    <Search className="w-4 h-4 text-[#666]" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name or email…"
                        className="flex-1 bg-transparent outline-none text-sm"
                        data-testid="admin-user-search"
                    />
                </div>
                <select
                    value={platformRole}
                    onChange={(e) => setPlatformRole(e.target.value)}
                    className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs"
                    data-testid="admin-user-platform-role-filter"
                >
                    <option value="" className="bg-[#141414]">Any platform role</option>
                    {PLATFORM_ROLES.map((r) => (
                        <option key={r.id} value={r.id} className="bg-[#141414]">{r.label}</option>
                    ))}
                </select>
                <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs"
                    data-testid="admin-user-status-filter"
                >
                    <option value="" className="bg-[#141414]">Any status</option>
                    <option value="active" className="bg-[#141414]">Active</option>
                    <option value="suspended" className="bg-[#141414]">Suspended</option>
                </select>
            </section>

            <div className="px-2 md:px-4">
                {loading ? (
                    <div className="p-8 font-mono text-xs text-[#666]" data-testid="admin-users-loading">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-xs font-mono uppercase text-[#666]" data-testid="admin-users-empty">
                        No users match.
                    </div>
                ) : (
                    <div className="border border-[#2a2a2a] mt-3">
                        {rows.map((u) => {
                            const pr = u.platform_role || (u.role === "admin" ? "admin" : "user");
                            const artistMems = u.artist_memberships || [];
                            const venueMems = u.venue_memberships || [];
                            const hasFanPassport = !!u.has_fan_passport;
                            return (
                                <div
                                    key={u.id}
                                    className="grid grid-cols-12 gap-3 items-start p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm"
                                    data-testid={`admin-user-row-${u.id}`}
                                >
                                    {/* Account */}
                                    <div className="col-span-12 md:col-span-4 min-w-0">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Account</div>
                                        <div className="font-display font-bold uppercase truncate mt-0.5">{u.name || u.email}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666] truncate" data-testid={`admin-user-email-${u.id}`}>{u.email}</div>
                                    </div>

                                    {/* Platform Authority */}
                                    <div className="col-span-6 md:col-span-3">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Platform role</div>
                                        <select
                                            value={pr}
                                            onChange={(e) => changePlatformRole(u, e.target.value)}
                                            className="w-full mt-1 bg-transparent border border-[#2a2a2a] px-2 py-1 font-mono uppercase text-[10px]"
                                            data-testid={`admin-user-platform-role-${u.id}`}
                                        >
                                            {PLATFORM_ROLES.map((r) => (
                                                <option key={r.id} value={r.id} className="bg-[#141414]">{r.label}</option>
                                            ))}
                                        </select>
                                        {pr === "admin" && (
                                            <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-mono uppercase text-[#ff9500]" data-testid={`admin-user-admin-badge-${u.id}`}>
                                                <ShieldAlert className="w-3 h-3" /> Platform Admin
                                            </div>
                                        )}
                                    </div>

                                    {/* Relationships + Fan Passport */}
                                    <div className="col-span-12 md:col-span-3 min-w-0">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Relationships</div>
                                        {artistMems.length === 0 && venueMems.length === 0 ? (
                                            <div className="text-[10px] font-mono uppercase text-[#666] mt-1" data-testid={`admin-user-no-relationships-${u.id}`}>None</div>
                                        ) : (
                                            <ul className="mt-1 space-y-0.5" data-testid={`admin-user-relationships-${u.id}`}>
                                                {artistMems.map((m) => (
                                                    <li
                                                        key={`art-${m.artist_id}`}
                                                        className="text-[10px] font-mono uppercase text-[#f4f4f0] truncate"
                                                        data-testid={`admin-user-relationship-artist-${u.id}-${m.artist_id}`}
                                                    >
                                                        {m.artist_name || m.artist_id} — <span className="text-[#ff4400]">{ARTIST_ROLE_LABEL[m.role] || m.role}</span>
                                                    </li>
                                                ))}
                                                {venueMems.map((m) => (
                                                    <li
                                                        key={`ven-${m.venue_id}`}
                                                        className="text-[10px] font-mono uppercase text-[#f4f4f0] truncate"
                                                        data-testid={`admin-user-relationship-venue-${u.id}-${m.venue_id}`}
                                                    >
                                                        {m.venue_name || m.venue_id} — <span className="text-[#ff4400]">{VENUE_ROLE_LABEL[m.role] || m.role}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        <div className="mt-2 text-[10px] font-mono uppercase" data-testid={`admin-user-fan-passport-${u.id}`}>
                                            <span className="text-[#666]">Fan Passport:</span>{" "}
                                            <span className={hasFanPassport ? "text-[#00e676]" : "text-[#a3a3a3]"}>
                                                {hasFanPassport ? "Active" : "Not activated"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Status + suspend + delete */}
                                    <div className="col-span-12 md:col-span-2 flex md:flex-col md:items-end gap-2 justify-between">
                                        <div
                                            className={`text-[10px] font-mono uppercase ${STATUS_COLOR[u.status] || "text-[#a3a3a3]"}`}
                                            data-testid={`admin-user-status-${u.id}`}
                                        >
                                            {u.status || "active"}
                                        </div>
                                        <div className="flex md:flex-col items-end gap-1">
                                            <button
                                                onClick={() => toggleStatus(u)}
                                                className="pm-btn text-[10px]"
                                                data-testid={`admin-user-toggle-${u.id}`}
                                            >
                                                {u.status === "suspended" ? (
                                                    <><ShieldCheck className="w-3 h-3 mr-1" /> Restore</>
                                                ) : (
                                                    <><ShieldOff className="w-3 h-3 mr-1" /> Suspend</>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(u)}
                                                className="pm-btn text-[10px] border-[#ff3333] text-[#ff3333]"
                                                data-testid={`admin-user-delete-${u.id}`}
                                            >
                                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {deleteTarget && (
                <DeleteUserModal
                    user={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onDeleted={() => { setDeleteTarget(null); reload(); }}
                />
            )}
        </div>
    );
}
